import { useCallback, useMemo } from 'react'
import type { Candle } from '@/components'

/**
 * Resolution of the synthetic index space the replay bars are driven with.
 *
 * The bars are index-based, but replay state is time-based with windowed loading, so the
 * configured range is projected onto a fixed number of "scrub buckets" and each scrub is
 * translated back to a timestamp.
 */
export const SCRUB_RESOLUTION = 1000

export interface UseScrubProjectionOptions {
  rangeStartTs: number
  rangeEndTs: number
  /** How far the engine has produced data, or null once the run is complete. */
  maxAvailableTs: number | null
  /** Replay cursor as a 0..1 fraction of the range. */
  progressViewPosition: number
  onScrubTime: (timestamp: number) => void
}

export interface ScrubProjection {
  /** Synthetic bars carrying only `time`, for the bar's scrub date labels. */
  scrubOhlc: Candle[]
  /** Thumb position, 1-based. */
  visibleCount: number
  totalCandles: number
  /** Cap so the user can never aim past data the run has actually produced. */
  maxScrubIndex: number
  handleScrub: (index: number) => void
}

/**
 * Bridge between a time-based replay cursor and an index-based transport bar.
 *
 * Extracted from BotBacktestTopSection so the Backtest tab's bar and the walkthrough's
 * transport share one projection — two copies would let the thumb and the cap drift apart.
 */
export default function useScrubProjection({
  rangeStartTs,
  rangeEndTs,
  maxAvailableTs,
  progressViewPosition,
  onScrubTime,
}: UseScrubProjectionOptions): ScrubProjection {
  const rangeTotal = rangeEndTs - rangeStartTs

  const scrubOhlc = useMemo<Candle[]>(
    () =>
      Array.from({ length: SCRUB_RESOLUTION }, (_, i) => ({
        time: rangeStartTs + (i / SCRUB_RESOLUTION) * rangeTotal,
        open: 0,
        high: 0,
        low: 0,
        close: 0,
      })),
    [rangeStartTs, rangeTotal],
  )

  // Always the replay (progressView) position. Chart-centre panning is a separate concern and
  // must not move the thumb.
  const visibleCount = Math.max(
    1,
    Math.min(SCRUB_RESOLUTION, Math.round(progressViewPosition * SCRUB_RESOLUTION)),
  )

  const maxScrubIndex = useMemo(() => {
    if (maxAvailableTs == null) return SCRUB_RESOLUTION
    const cappedTs = Math.min(maxAvailableTs, rangeEndTs)
    const fraction = Math.max(0, Math.min(1, (cappedTs - rangeStartTs) / rangeTotal))
    return Math.max(1, Math.round(fraction * SCRUB_RESOLUTION))
  }, [maxAvailableTs, rangeStartTs, rangeEndTs, rangeTotal])

  const handleScrub = useCallback(
    (idx: number) => {
      const cappedIdx = Math.min(idx, maxScrubIndex)
      const fraction = Math.max(0, Math.min(1, cappedIdx / SCRUB_RESOLUTION))
      onScrubTime(rangeStartTs + fraction * rangeTotal)
    },
    [onScrubTime, rangeStartTs, rangeTotal, maxScrubIndex],
  )

  return { scrubOhlc, visibleCount, totalCandles: SCRUB_RESOLUTION, maxScrubIndex, handleScrub }
}
